import { useCallback } from "react"
import { useTranslation } from "react-i18next"
import { IPermissionCode, usePermission } from "../permission"

/**
 * Inbound orders: the ONE place that says which action exists in which status and which permission
 * it needs (canvas Ord_Actions + Ord_InboundDrawer, same shape as delivery-actions.ts). Codes are the
 * backend's (docs/plans/2026-10-20-order-admin-phase2-be-contract.md §1 «Орлого»). An action that
 * applies but is not allowed stays visible, aria-disabled, with «Эрх хэрэгтэй: КОД».
 */
export type InboundAction =
  | "view"
  | "create"
  | "approve"
  | "dispatch"
  | "pickupStart"
  | "pickupComplete"
  | "arrive"
  | "receive"
  | "cancel"
  | "exceptionRaise"
  | "exceptionStart"
  | "exceptionResolve"
  | "exceptionClose"
  | "repostStock"

export const INBOUND_ACTION_PERMISSION: Record<InboundAction, IPermissionCode> = {
  view: "IBN_CREATE", // list.ts, get.ts, exceptions.ts
  create: "IBN_CREATE", // create.ts, update.ts
  approve: "IBN_APPROVE",
  dispatch: "IBN_DISPATCH",
  pickupStart: "IBN_ASSIGN",
  pickupComplete: "IBN_RECEIVE",
  arrive: "IBN_ASSIGN",
  receive: "IBN_RECEIVE",
  cancel: "IBN_CANCEL",
  exceptionRaise: "IBN_RECEIVE",
  exceptionStart: "IBN_RECEIVE",
  exceptionResolve: "IBN_RECEIVE",
  exceptionClose: "IBN_RECEIVE",
  repostStock: "IBN_RECEIVE",
}

export type InboundActionSubject = {
  /** Order status. */
  status?: string | null
  /** "Pickup started" = DISPATCHED_PICKUP with custodyTransferOutboundId set (contract §2 O134). */
  pickupStarted?: boolean
  /** Exception status and type, for the exception actions. */
  exceptionStatus?: string | null
  exceptionType?: string | null
}

const OPEN_EXCEPTION = ["OPEN", "IN_PROGRESS"]
const NON_TERMINAL = ["DRAFT", "APPROVED", "DISPATCHED_PICKUP", "PICKUP_COMPLETED", "IN_TRANSIT", "AT_DESTINATION"]

/** Status x action: false = the action does not belong here (render nothing). */
const APPLIES: Record<InboundAction, (s: InboundActionSubject) => boolean> = {
  view: () => true,
  create: () => true,
  approve: (s) => s.status === "DRAFT",
  dispatch: (s) => s.status === "APPROVED",
  pickupStart: (s) => s.status === "DISPATCHED_PICKUP" && !s.pickupStarted,
  // Pickup-complete needs DISPATCHED_PICKUP and a started pickup; PICKUP_COMPLETED is never written.
  pickupComplete: (s) => s.status === "DISPATCHED_PICKUP" && !!s.pickupStarted,
  arrive: (s) => s.status === "IN_TRANSIT",
  receive: (s) => s.status === "AT_DESTINATION",
  cancel: (s) => NON_TERMINAL.includes(s.status ?? ""),
  exceptionRaise: () => true,
  exceptionStart: (s) => s.exceptionStatus === "OPEN",
  exceptionResolve: (s) => s.exceptionType !== "STOCK_POST_FAILED" && OPEN_EXCEPTION.includes(s.exceptionStatus ?? ""),
  exceptionClose: (s) => s.exceptionStatus === "RESOLVED" || (s.exceptionStatus === "OPEN" && s.exceptionType !== "STOCK_POST_FAILED"),
  repostStock: (s) => s.exceptionType === "STOCK_POST_FAILED" && OPEN_EXCEPTION.includes(s.exceptionStatus ?? ""),
}

export type InboundActionState = {
  applies: boolean
  allowed: boolean
  /** «Эрх хэрэгтэй: КОД» when the permission is missing, null when allowed. */
  reason: string | null
}

export function inboundActionApplies(action: InboundAction, subject: InboundActionSubject | null | undefined): boolean {
  return !!subject && APPLIES[action](subject)
}

export function useInboundActions() {
  const { t } = useTranslation("common")
  const granted: Partial<Record<IPermissionCode, boolean>> = {
    IBN_CREATE: usePermission("IBN_CREATE").hasAccess,
    IBN_APPROVE: usePermission("IBN_APPROVE").hasAccess,
    IBN_DISPATCH: usePermission("IBN_DISPATCH").hasAccess,
    IBN_ASSIGN: usePermission("IBN_ASSIGN").hasAccess,
    IBN_RECEIVE: usePermission("IBN_RECEIVE").hasAccess,
    IBN_CANCEL: usePermission("IBN_CANCEL").hasAccess,
  }

  const permissionState = useCallback(
    (action: InboundAction) => {
      const code = INBOUND_ACTION_PERMISSION[action]
      const allowed = !!granted[code]
      return { allowed, reason: allowed ? null : t("permission.required", { code }) }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, JSON.stringify(granted)],
  )

  const stateOf = useCallback(
    (action: InboundAction, subject: InboundActionSubject | null | undefined): InboundActionState => ({
      applies: inboundActionApplies(action, subject),
      ...permissionState(action),
    }),
    [permissionState],
  )

  return { stateOf, permissionState }
}
